import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Counts down to `targetIso` using the server's clock: `serverNowIso` (sent
 * alongside the target) corrects for a till whose local clock is wrong.
 * Returns null when there is no target. `onElapsed` fires once when the
 * countdown reaches zero.
 */
export const useServerCountdown = (
  targetIso?: string | null,
  serverNowIso?: string | null,
  onElapsed?: () => void
): number | null => {
  const offsetMs = useMemo(() => {
    const serverNow = serverNowIso ? Date.parse(serverNowIso) : NaN;
    return Number.isNaN(serverNow) ? 0 : serverNow - Date.now();
  }, [serverNowIso]);

  const target = targetIso ? Date.parse(targetIso) : NaN;
  const remainingNow = () =>
    Number.isNaN(target) ? null : Math.max(0, target - (Date.now() + offsetMs));

  const [remainingMs, setRemainingMs] = useState<number | null>(remainingNow);
  const elapsedFiredRef = useRef(false);
  const onElapsedRef = useRef(onElapsed);
  onElapsedRef.current = onElapsed;

  useEffect(() => {
    elapsedFiredRef.current = false;
    setRemainingMs(remainingNow());
    if (Number.isNaN(target)) return;

    const interval = setInterval(() => {
      const remaining = remainingNow();
      setRemainingMs(remaining);
      if (remaining === 0 && !elapsedFiredRef.current) {
        elapsedFiredRef.current = true;
        onElapsedRef.current?.();
      }
    }, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, offsetMs]);

  return remainingMs;
};

/** "5h 12m" for an hour or more, otherwise "14:32" (mm:ss). */
export const formatCountdown = (ms: number): string => {
  const totalSeconds = Math.ceil(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

/** Wall-clock time of `iso` in the store's time zone, e.g. "12:00 AM". */
export const formatStoreTime = (iso: string, timeZone?: string | null): string => {
  try {
    return new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      timeZone: timeZone || undefined,
    }).format(new Date(iso));
  } catch {
    return new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" }).format(
      new Date(iso)
    );
  }
};
